import { useEffect, useReducer } from 'react';
import {
  Configure,
  useInstantSearch,
  useToggleRefinement,
} from 'react-instantsearch';
import InstantSearchAccordion from './units/instantSearchAccordion';
import { useLocationData } from '@javascript/lib/hooks/useLocationData';
import { ValidPostcode } from '@javascript/lib/regularExpressions';
import useQueryParam from '@javascript/lib/hooks/useQueryParam';
import InstantSearchFilterOption from './units/instantSearchFilterOption';
import useCurrentSortOrder from '../hooks/useCurrentSortOrder';
import GeolocationButton from '@javascript/components/application/input/locationFinder/geolocationButton';
import { FilterBaseProps } from '@javascript/types/InstantSearch';
import { useSearchClient } from '../provider/searchClientProvider';

const RADIUS_OPTIONS: Array<number | 'Any'> = ['Any', 10, 20, 50, 100, 200];

type State = {
  error: boolean;
  postcode: string;
  selectedRadius: number | 'Any';
};

type Action =
  | { payload: string; type: 'SET_POSTCODE' }
  | { payload: boolean; type: 'SET_ERROR' }
  | { payload: number | 'Any'; type: 'SET_RADIUS' }
  | { payload: State; type: 'RESET' };

const initialState = (
  distanceParam: string,
  postcodeParam: string,
  showClickAndCollectOptions: boolean
): State => {
  let selectedRadius: number | 'Any';

  if (showClickAndCollectOptions) {
    selectedRadius = 'Any';
  } else if (distanceParam) {
    selectedRadius = parseInt(distanceParam);
  } else {
    selectedRadius = 'Any';
  }

  return {
    postcode: postcodeParam || '',
    error: false,
    selectedRadius,
  };
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_POSTCODE':
      return { ...state, postcode: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_RADIUS':
      return { ...state, selectedRadius: action.payload };
    case 'RESET':
      return action.payload;
    default:
      return state;
  }
};

const LocationFilter = ({ filtersOpen }: FilterBaseProps) => {
  const { setUiState } = useInstantSearch();
  const { indexName } = useSearchClient();
  const [distanceParam, updateDistanceParam] = useQueryParam('distance');
  const index = useCurrentSortOrder();
  const { location, postcodeParam, updatePostcodeParam } = useLocationData();
  const { value, refine } = useToggleRefinement({
    attribute: 'clickAndCollectAvailable',
  });
  const showClickAndCollectOptions = value.isRefined;

  const [state, dispatch] = useReducer(
    reducer,
    initialState(distanceParam, postcodeParam, showClickAndCollectOptions)
  );

  const isValidPostcode =
    ValidPostcode.test(state.postcode) ||
    ValidPostcode.test(postcodeParam || '');

  const getRadiusInMeters = (radius: number | 'Any'): number | undefined => {
    const MILES_TO_METERS = 1609;
    const UK_RADIUS_IN_MILES = 700;

    if (radius === 'Any') {
      return UK_RADIUS_IN_MILES * MILES_TO_METERS;
    }

    return radius * MILES_TO_METERS;
  };

  const getAroundPrecision = (): number | undefined => {
    const AROUND_PRECISION_METERS = 1609;

    if (index?.includes('geolocation')) {
      return undefined;
    }

    return AROUND_PRECISION_METERS;
  };

  useEffect(() => {
    if (!postcodeParam) {
      const resetState = initialState('', '', false);
      dispatch({ type: 'RESET', payload: resetState });
      updateDistanceParam('');
      updatePostcodeParam('');
    }
  }, [postcodeParam]);

  const validateAndSetPostcode = () => {
    if (
      (state.postcode === '' && !showClickAndCollectOptions) ||
      isValidPostcode
    ) {
      updatePostcodeParam(state.postcode);
      if (state.error) dispatch({ type: 'SET_ERROR', payload: false });
      return;
    }
    dispatch({ type: 'SET_ERROR', payload: true });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    validateAndSetPostcode();
  };

  const handlePostcodeChange = (value: string) => {
    const sanitizedValue = value.replace(/[^a-zA-Z0-9\s]/g, '');

    if (state.error) {
      dispatch({ type: 'SET_ERROR', payload: false });
    }

    dispatch({ type: 'SET_POSTCODE', payload: sanitizedValue });
  };

  const handleGeolocationButton = (value: string) => {
    updatePostcodeParam(value);
    dispatch({ type: 'SET_POSTCODE', payload: value });
    setUiState((prev) => ({
      ...prev,
      [indexName]: {
        ...prev[indexName],
        page: 0,
      },
    }));
  };

  const handleRadiusSelect = (radius: number | 'Any') => {
    dispatch({ type: 'SET_RADIUS', payload: radius });
    const distanceValue = radius === 'Any' ? '' : radius.toString();
    updateDistanceParam(distanceValue);

    setUiState((prev) => ({
      ...prev,
      [indexName]: {
        ...prev[indexName],
        page: 0,
      },
    }));
  };

  const checkIfBrowserSupportsGeolocation = () => {
    return navigator.geolocation && navigator.geolocation.getCurrentPosition;
  };

  const isLocationValid =
    location.lat !== undefined && location.lng !== undefined;

  const geoConfig = isLocationValid
    ? {
        aroundLatLng: `${location.lat},${location.lng}`,
        aroundPrecision: getAroundPrecision(),
        aroundRadius: getRadiusInMeters(state.selectedRadius),
      }
    : {};

  const selectedOptions = !state.postcode
    ? ''
    : `${
        state.selectedRadius === 'Any'
          ? 'Any distance'
          : `Up to ${state.selectedRadius} miles`
      } from ${state.postcode}`;

  return (
    <InstantSearchAccordion
      filtersOpen={filtersOpen}
      label={'Distance from you'}
      selectedOptions={selectedOptions}
    >
      <form
        className={`ch-form__group ch-mb--3 ${
          state.error && 'ch-form__group--error'
        }`}
        onSubmit={handleSubmit}
      >
        <div className="ch-display--flex ch-flex-flow--column-nowrap">
          <label className="ch-pt--2 ch-fs--3">Your postcode</label>
          <p className="ch-mb--0 ch-fs--3 ch-fw--400 ch-color--grey-6">
            Example, G52 4FH
          </p>
        </div>
        <span className="ch-form__control-validation" role="alert">
          Please enter a full, valid postcode.
        </span>
        <div className="ac-location__group ch-mb--3">
          {checkIfBrowserSupportsGeolocation() && (
            <GeolocationButton
              onSuccess={({ location }: { location: string }) =>
                handleGeolocationButton(location)
              }
              source="vehicle search"
            />
          )}
          <input
            className="ch-form__control ch-pl--5 ch-bc--grey-6"
            id="location-postcode"
            onBlur={validateAndSetPostcode}
            onChange={(e) => handlePostcodeChange(e.target.value)}
            type="text"
            value={state.postcode}
          />
        </div>
        <div className="click-and-collect__toggle">
          <input
            checked={showClickAndCollectOptions}
            className="ch-checkbox"
            id="toggle-click-and-collect"
            onChange={(e) => {
              if (state.error) {
                return;
              }
              if (isValidPostcode) {
                dispatch({ type: 'SET_RADIUS', payload: 'Any' });
                updateDistanceParam('');
                refine({ isRefined: !e.target.checked });
              } else {
                dispatch({ type: 'SET_ERROR', payload: true });
              }
            }}
            type="checkbox"
          />
          <label
            className="ch-checkbox__label ch-checkbox__label--primary ch-checkbox__label--hide-checkbox ch-display--flex ch-justify-content--between"
            htmlFor="toggle-click-and-collect"
          >
            <p className="ch-text--normal ch-mb--0 ch-fs--3">
              Show <b className="ch-text--bold">Click and Collect</b> options
            </p>
            <span className="ch-toggle ch-ml--2"></span>
          </label>
        </div>
        {isValidPostcode && !showClickAndCollectOptions && (
          <div className="ch-display--flex ch-flex-flow--column-nowrap ch-mt--5">
            <label
              className="ch-text--left ch-fs--3 ch-fw--500 ch-mb--2"
              htmlFor="radius"
            >
              Show cars within
            </label>
            <ul>
              {RADIUS_OPTIONS.map((radius) => (
                <InstantSearchFilterOption.Radio
                  attribute="radius"
                  isSelected={radius === state.selectedRadius}
                  key={`location-${radius}`}
                  label={radius === 'Any' ? radius : `${radius} miles`}
                  onChange={() => handleRadiusSelect(radius)}
                />
              ))}
            </ul>
          </div>
        )}
      </form>
      <Configure {...geoConfig} getRankingInfo />
    </InstantSearchAccordion>
  );
};

export default LocationFilter;
